import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';

// Public submission view — does NOT use the AuthContext or send cookies.
const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/public`,
  headers: { 'Content-Type': 'application/json' },
});

const PublicForm = ({ slug }) => {
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    publicApi.get(`/forms/${slug}`).then((r) => setForm(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Form not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  const set = (id, v) => setValues((s) => ({ ...s, [id]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      // Build payload using both id-keyed and name-keyed for backend mapping
      const payload = { ...values };
      (form.fields || []).forEach((f) => {
        if (f.name && values[f.id] != null) payload[f.name] = values[f.id];
      });
      await publicApi.post(`/forms/${slug}/submit`, payload);
      setSubmitted(true);
      if (form?.redirect_url) {
        window.location.href = form.redirect_url;
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Submission failed');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="fp-shell"><div className="fp-card">Loading…</div></div>;
  if (err && !form) return <div className="fp-shell"><div className="fp-card" data-testid="public-form-error">{err}</div></div>;
  if (!form) return null;

  if (submitted) {
    return (
      <div className="fp-shell">
        <div className="fp-card fp-success" data-testid="public-form-success">
          <div className="ic"><CheckCircle2 size={44} /></div>
          <div className="fp-title" style={{ marginBottom: 8 }}>{form.title}</div>
          <div style={{ fontSize: 13, color: 'var(--text-sub)' }}>{form.success_message}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fp-shell">
      <form className="fp-card" onSubmit={submit} data-testid="public-form">
        <div className="fp-title">{form.title}</div>
        {form.description && <div className="fp-desc">{form.description}</div>}
        {(form.fields || []).map((f) => <FieldRender key={f.id} field={f} value={values[f.id]} onChange={(v) => set(f.id, v)} />)}
        {err && <div className="f-error">{err}</div>}
        <button type="submit" className="fp-submit" disabled={busy} data-testid="public-form-submit">
          {busy ? 'Submitting…' : (form.submit_label || 'Submit')}
        </button>
        <div className="fp-foot">Powered by LegacyCRM</div>
      </form>
    </div>
  );
};

export const FieldRender = ({ field, value, onChange, disabled }) => {
  const t = field.type;
  if (t === 'heading') return <div className="fp-heading">{field.label}</div>;
  if (t === 'divider') return <hr className="fp-divider" />;
  const common = {
    required: !!field.required,
    placeholder: field.placeholder || '',
    'data-testid': `field-${field.id}`,
    disabled,
  };
  return (
    <div className="fp-field">
      <label>{field.label}{field.required && <span className="req"> *</span>}</label>
      {t === 'textarea' ? (
        <textarea className="fp-textarea" rows={4} value={value || ''} onChange={(e) => onChange(e.target.value)} {...common} />
      ) : t === 'select' ? (
        <select className="fp-select" value={value || ''} onChange={(e) => onChange(e.target.value)} {...common}>
          <option value="">— Select —</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : t === 'radio' ? (
        <div>
          {(field.options || []).map((o) => (
            <label key={o} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 12, fontSize: 12 }}>
              <input type="radio" name={field.id} value={o} checked={value === o} onChange={() => onChange(o)} {...common} />{o}
            </label>
          ))}
        </div>
      ) : t === 'checkbox' ? (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} {...common} />{field.placeholder || field.label}
        </label>
      ) : t === 'date' ? (
        <input type="date" className="fp-input" value={value || ''} onChange={(e) => onChange(e.target.value)} {...common} />
      ) : t === 'number' ? (
        <input type="number" className="fp-input" value={value || ''} onChange={(e) => onChange(e.target.value)} {...common} />
      ) : t === 'signature' ? (
        <input type="text" className="fp-input" placeholder="Type your full name as signature" value={value || ''} onChange={(e) => onChange(e.target.value)} style={{ fontFamily: 'cursive', fontSize: 16 }} {...common} />
      ) : (
        <input
          type={t === 'email' ? 'email' : t === 'phone' ? 'tel' : 'text'}
          className="fp-input"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          {...common}
        />
      )}
      {field.help && <div className="fp-help">{field.help}</div>}
    </div>
  );
};

export default PublicForm;
